import HomepageWhatsAppContact from "./HomepageWhatsAppContact";
import React, { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowDown,
  CheckCircle2,
  Droplets,
  Factory,
  Flame,
  FlaskConical,
  MapPin,
  MessageCircle,
  Microscope,
  Route,
  ShieldCheck,
  Truck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage, type Language } from "@/contexts/LanguageContext";
import LiquidHeroImage from "@/components/LiquidHeroImage";

const WHATSAPP_NUMBER = "5537998606150";

const coverageGroups = [
  { region: "Sudeste", states: "MG · SP · RJ · ES" },
  { region: "Sul", states: "PR · SC · RS" },
  { region: "Centro-Oeste", states: "GO · MS" },
  { region: "Nordeste", states: "BA" },
  { region: "Norte", states: "PA" },
];

const content = {
  pt: {
    eyebrow: "QUIMICAL · SOLUÇÕES EM CAL INDUSTRIAL",
    heroTitle: "Comprar cal industrial exige mais do que comparar preço por tonelada.",
    heroText:
      "A Quimical conecta a necessidade da sua operação ao produto, fornecimento e logística adequados — com foco em especificação, disponibilidade e continuidade.",
    whatsapp: "Falar com a Quimical",
    scroll: "Role para entender",
    painsEyebrow: "A DECISÃO DE COMPRA",
    painsTitle: "A cal certa precisa funcionar no processo e chegar quando sua operação precisa.",
    painsIntro:
      "Na compra industrial, produto, especificação, homologação, prazo e logística precisam funcionar em conjunto.",
    pains: [
      {
        title: "Especificação incompatível",
        text: "Composição, granulometria e reatividade podem alterar o comportamento da cal no processo industrial.",
      },
      {
        title: "Disponibilidade e prazo",
        text: "Preço competitivo perde valor quando o produto não está disponível no momento em que a operação precisa.",
      },
      {
        title: "Distância e logística",
        text: "Frete, origem disponível, acesso e programação de descarga fazem parte da decisão de compra.",
      },
      {
        title: "Homologação e continuidade",
        text: "Uma alternativa comercial só é útil quando atende aos requisitos técnicos e pode sustentar o fornecimento.",
      },
    ],
    solutionEyebrow: "ONDE A QUIMICAL ENTRA",
    solutionTitle: "Da necessidade à entrega.",
    solutionText:
      "A Quimical atua na interface entre o comprador industrial, o produto adequado, fornecedores qualificados e a logística da operação.",
    flow: ["Necessidade", "Especificação", "Produto", "Fornecedor", "Logística", "Entrega"],
    calcEyebrow: "ENTENDA O PRODUTO",
    calcTitle: "O desempenho da cal começa antes da compra: começa na calcinação.",
    calcIntro:
      "A matéria-prima e as condições de processo influenciam composição, reatividade, granulometria e desempenho. Por isso, cal não é uma commodity perfeitamente uniforme.",
    calcStages: [
      {
        label: "01 · Calcário",
        title: "A matéria-prima define a base química.",
        text: "O calcário fornece o carbonato de cálcio e, conforme sua natureza, outros componentes minerais relevantes.",
      },
      {
        label: "02 · Calcinação",
        title: "O calor transforma o carbonato.",
        text: "No forno, o carbonato de cálcio se decompõe e origina o óxido de cálcio — a cal virgem.",
      },
      {
        label: "03 · Cal virgem",
        title: "CaCO₃ → CaO + CO₂",
        text: "O CaO resultante pode apresentar características diferentes conforme matéria-prima, forno, temperatura e controle do processo.",
      },
      {
        label: "04 · Hidratação",
        title: "CaO + H₂O → Ca(OH)₂",
        text: "Ao reagir de forma controlada com água, a cal virgem dá origem à cal hidratada.",
      },
    ],
    limeEyebrow: "TIPOS DE CAL",
    limeTitle: "Produtos diferentes para necessidades diferentes.",
    limeText:
      "A escolha deve considerar composição, forma física, especificação e aplicação. A Quimical ajuda a relacionar esses critérios à necessidade da operação.",
    limeTypes: [
      {
        name: "Cal calcítica virgem",
        formula: "CaO",
        applications: "Siderurgia · saneamento · mineração · processos químicos",
      },
      {
        name: "Cal calcítica hidratada",
        formula: "Ca(OH)₂",
        applications: "Tratamento de água · química · construção · controle de pH",
      },
      {
        name: "Cal dolomítica virgem",
        formula: "CaO + MgO",
        applications: "Siderurgia · refratários · aplicações minerais e industriais",
      },
      {
        name: "Cal dolomítica hidratada",
        formula: "Ca(OH)₂ + Mg(OH)₂",
        applications: "Aplicações específicas que demandam cálcio e magnésio",
      },
    ],
    coverageEyebrow: "ATENDIMENTO",
    coverageTitle: "Da necessidade da sua planta até a alternativa logística viável.",
    coverageText:
      "A Quimical registra operações concluídas em diferentes regiões do Brasil. A disponibilidade é analisada conforme produto, origem possível, destino e condição logística.",
    coverageNote:
      "A cobertura abaixo mostra UFs com entregas concluídas registradas em 2026. Não exibe clientes, fornecedores, volumes, origens ou rotas.",
    checkLocation: "Consultar atendimento no meu município",
    diffEyebrow: "DIFERENCIAIS",
    diffTitle: "A compra é tratada como uma decisão técnica e logística, não apenas como uma cotação.",
    diffs: [
      {
        title: "Leitura da aplicação",
        text: "A necessidade do processo vem antes da indicação do produto.",
      },
      {
        title: "Comparação técnica",
        text: "Especificação e características do produto são consideradas na seleção da alternativa.",
      },
      {
        title: "Alternativas de fornecimento",
        text: "A operação não fica limitada a uma única possibilidade de origem quando existem opções compatíveis.",
      },
      {
        title: "Logística integrada à decisão",
        text: "Destino, prazo, disponibilidade e condição de descarga fazem parte da análise comercial.",
      },
      {
        title: "Amostra e carga teste",
        text: "Quando necessário, a validação pode avançar por amostra ou carga teste antes da recorrência.",
      },
      {
        title: "Acompanhamento comercial",
        text: "A Quimical permanece na interface entre necessidade, fornecimento e entrega.",
      },
    ],
    leadEyebrow: "PRÓXIMO PASSO",
    leadTitle: "Qual cal sua operação precisa?",
    leadText: "Converse com nossa equipe pelo WhatsApp sobre a necessidade da sua operação.",
    company: "Empresa",
    city: "Cidade / UF",
    application: "Aplicação",
    volume: "Consumo aproximado por mês (opcional)",
    product: "Produto, se souber (opcional)",
    companyPlaceholder: "Nome da empresa",
    cityPlaceholder: "Ex.: Divinópolis / MG",
    applicationPlaceholder: "Ex.: tratamento de água, siderurgia, mineração...",
    volumePlaceholder: "Ex.: 200 t/mês",
    productPlaceholder: "Ex.: cal calcítica virgem",
    formButton: "Continuar pelo WhatsApp",
    privacy:
      "Os campos servem apenas para montar a mensagem no seu dispositivo. O site não envia esses dados antes de você abrir o WhatsApp.",
    messageIntro: "Olá, gostaria de falar com a Quimical sobre fornecimento de cal industrial.",
  },
  en: {
    eyebrow: "QUIMICAL · INDUSTRIAL LIME SOLUTIONS",
    heroTitle: "Buying industrial lime requires more than comparing price per tonne.",
    heroText:
      "Quimical connects your operation's needs to the right product, supply and logistics — focusing on specification, availability and continuity.",
    whatsapp: "Talk to Quimical",
    scroll: "Scroll to understand",
    painsEyebrow: "THE BUYING DECISION",
    painsTitle: "The right lime must work in the process and arrive when your operation needs it.",
    painsIntro:
      "In industrial purchasing, product, specification, approval, lead time and logistics need to work together.",
    pains: [
      { title: "Specification mismatch", text: "Composition, particle size and reactivity can change lime behaviour in an industrial process." },
      { title: "Availability and lead time", text: "A competitive price loses value when the product is not available when the operation needs it." },
      { title: "Distance and logistics", text: "Freight, available source, access and unloading schedule are part of the buying decision." },
      { title: "Approval and continuity", text: "A commercial alternative is useful only when it meets technical requirements and can sustain supply." },
    ],
    solutionEyebrow: "WHERE QUIMICAL FITS",
    solutionTitle: "From need to delivery.",
    solutionText:
      "Quimical works at the interface between the industrial buyer, the appropriate product, qualified suppliers and the operation's logistics.",
    flow: ["Need", "Specification", "Product", "Supplier", "Logistics", "Delivery"],
    calcEyebrow: "UNDERSTAND THE PRODUCT",
    calcTitle: "Lime performance starts before the purchase: it starts with calcination.",
    calcIntro:
      "Raw material and process conditions influence composition, reactivity, particle size and performance. Lime is therefore not a perfectly uniform commodity.",
    calcStages: [
      { label: "01 · Limestone", title: "Raw material defines the chemical base.", text: "Limestone supplies calcium carbonate and, depending on its nature, other relevant mineral components." },
      { label: "02 · Calcination", title: "Heat transforms the carbonate.", text: "In the kiln, calcium carbonate decomposes and forms calcium oxide — quicklime." },
      { label: "03 · Quicklime", title: "CaCO₃ → CaO + CO₂", text: "The resulting CaO can vary according to raw material, kiln, temperature and process control." },
      { label: "04 · Hydration", title: "CaO + H₂O → Ca(OH)₂", text: "When quicklime reacts with water under controlled conditions, hydrated lime is formed." },
    ],
    limeEyebrow: "LIME TYPES",
    limeTitle: "Different products for different needs.",
    limeText: "Selection should consider composition, physical form, specification and application. Quimical helps connect these criteria to the operation's needs.",
    limeTypes: [
      { name: "Calcitic quicklime", formula: "CaO", applications: "Steel · sanitation · mining · chemical processes" },
      { name: "Calcitic hydrated lime", formula: "Ca(OH)₂", applications: "Water treatment · chemicals · construction · pH control" },
      { name: "Dolomitic quicklime", formula: "CaO + MgO", applications: "Steel · refractories · mineral and industrial applications" },
      { name: "Dolomitic hydrated lime", formula: "Ca(OH)₂ + Mg(OH)₂", applications: "Specific applications requiring calcium and magnesium" },
    ],
    coverageEyebrow: "SERVICE AREA",
    coverageTitle: "From your plant's need to a viable logistics alternative.",
    coverageText: "Quimical has completed operations recorded across different regions of Brazil. Availability is assessed according to product, possible source, destination and logistics conditions.",
    coverageNote: "The coverage below shows states with completed deliveries recorded in 2026. It does not expose customers, suppliers, volumes, origins or routes.",
    checkLocation: "Check service in my city",
    diffEyebrow: "DIFFERENTIALS",
    diffTitle: "Purchasing is treated as a technical and logistics decision, not only as a quotation.",
    diffs: [
      { title: "Application assessment", text: "Process needs come before product recommendation." },
      { title: "Technical comparison", text: "Specification and product characteristics are considered when selecting an alternative." },
      { title: "Supply alternatives", text: "The operation is not limited to a single possible source when compatible options exist." },
      { title: "Logistics in the decision", text: "Destination, lead time, availability and unloading conditions are part of the commercial analysis." },
      { title: "Sample and trial load", text: "When necessary, validation can progress through a sample or trial load before recurring supply." },
      { title: "Commercial follow-up", text: "Quimical remains at the interface between need, supply and delivery." },
    ],
    leadEyebrow: "NEXT STEP",
    leadTitle: "Which lime does your operation need?",
    leadText: "Talk to our team on WhatsApp about your operation’s requirements.",
    company: "Company", city: "City / State", application: "Application", volume: "Approximate monthly consumption (optional)", product: "Product, if known (optional)",
    companyPlaceholder: "Company name", cityPlaceholder: "Example: São Paulo / SP", applicationPlaceholder: "Example: water treatment, steel, mining...", volumePlaceholder: "Example: 200 t/month", productPlaceholder: "Example: calcitic quicklime", formButton: "Continue on WhatsApp",
    privacy: "The fields are used only to prepare the message on your device. The website does not send this data before you open WhatsApp.",
    messageIntro: "Hello, I would like to talk to Quimical about industrial lime supply.",
  },
  es: {
    eyebrow: "QUIMICAL · SOLUCIONES EN CAL INDUSTRIAL",
    heroTitle: "Comprar cal industrial exige más que comparar el precio por tonelada.",
    heroText: "Quimical conecta las necesidades de su operación con el producto, suministro y logística adecuados, con foco en especificación, disponibilidad y continuidad.",
    whatsapp: "Hablar con Quimical",
    scroll: "Deslice para entender",
    painsEyebrow: "LA DECISIÓN DE COMPRA",
    painsTitle: "La cal correcta debe funcionar en el proceso y llegar cuando su operación la necesita.",
    painsIntro: "En la compra industrial, producto, especificación, homologación, plazo y logística deben funcionar en conjunto.",
    pains: [
      { title: "Especificación incompatible", text: "La composición, granulometría y reactividad pueden cambiar el comportamiento de la cal en el proceso industrial." },
      { title: "Disponibilidad y plazo", text: "Un precio competitivo pierde valor cuando el producto no está disponible cuando la operación lo necesita." },
      { title: "Distancia y logística", text: "Flete, origen disponible, acceso y programación de descarga forman parte de la decisión de compra." },
      { title: "Homologación y continuidad", text: "Una alternativa comercial solo es útil cuando cumple los requisitos técnicos y puede sostener el suministro." },
    ],
    solutionEyebrow: "DÓNDE ENTRA QUIMICAL",
    solutionTitle: "De la necesidad a la entrega.",
    solutionText: "Quimical actúa en la interfaz entre el comprador industrial, el producto adecuado, proveedores calificados y la logística de la operación.",
    flow: ["Necesidad", "Especificación", "Producto", "Proveedor", "Logística", "Entrega"],
    calcEyebrow: "ENTIENDA EL PRODUCTO",
    calcTitle: "El desempeño de la cal comienza antes de la compra: comienza en la calcinación.",
    calcIntro: "La materia prima y las condiciones del proceso influyen en composición, reactividad, granulometría y desempeño. Por eso, la cal no es una commodity perfectamente uniforme.",
    calcStages: [
      { label: "01 · Caliza", title: "La materia prima define la base química.", text: "La caliza aporta carbonato de calcio y, según su naturaleza, otros componentes minerales relevantes." },
      { label: "02 · Calcinación", title: "El calor transforma el carbonato.", text: "En el horno, el carbonato de calcio se descompone y origina óxido de calcio — cal viva." },
      { label: "03 · Cal viva", title: "CaCO₃ → CaO + CO₂", text: "El CaO resultante puede variar según la materia prima, el horno, la temperatura y el control del proceso." },
      { label: "04 · Hidratación", title: "CaO + H₂O → Ca(OH)₂", text: "Cuando la cal viva reacciona con agua de forma controlada, se forma cal hidratada." },
    ],
    limeEyebrow: "TIPOS DE CAL",
    limeTitle: "Productos diferentes para necesidades diferentes.",
    limeText: "La elección debe considerar composición, forma física, especificación y aplicación. Quimical ayuda a relacionar estos criterios con la necesidad de la operación.",
    limeTypes: [
      { name: "Cal calcítica viva", formula: "CaO", applications: "Siderurgia · saneamiento · minería · procesos químicos" },
      { name: "Cal calcítica hidratada", formula: "Ca(OH)₂", applications: "Tratamiento de agua · química · construcción · control de pH" },
      { name: "Cal dolomítica viva", formula: "CaO + MgO", applications: "Siderurgia · refractarios · aplicaciones minerales e industriales" },
      { name: "Cal dolomítica hidratada", formula: "Ca(OH)₂ + Mg(OH)₂", applications: "Aplicaciones específicas que requieren calcio y magnesio" },
    ],
    coverageEyebrow: "ATENCIÓN",
    coverageTitle: "De la necesidad de su planta a una alternativa logística viable.",
    coverageText: "Quimical registra operaciones concluidas en diferentes regiones de Brasil. La disponibilidad se analiza según producto, posible origen, destino y condición logística.",
    coverageNote: "La cobertura muestra estados con entregas concluidas registradas en 2026. No expone clientes, proveedores, volúmenes, orígenes ni rutas.",
    checkLocation: "Consultar atención en mi ciudad",
    diffEyebrow: "DIFERENCIALES",
    diffTitle: "La compra se trata como una decisión técnica y logística, no solo como una cotización.",
    diffs: [
      { title: "Lectura de la aplicación", text: "La necesidad del proceso viene antes de la recomendación del producto." },
      { title: "Comparación técnica", text: "La especificación y las características del producto se consideran al seleccionar la alternativa." },
      { title: "Alternativas de suministro", text: "La operación no queda limitada a un único origen posible cuando existen opciones compatibles." },
      { title: "Logística integrada", text: "Destino, plazo, disponibilidad y condición de descarga forman parte del análisis comercial." },
      { title: "Muestra y carga de prueba", text: "Cuando es necesario, la validación puede avanzar mediante muestra o carga de prueba antes de la recurrencia." },
      { title: "Seguimiento comercial", text: "Quimical permanece en la interfaz entre necesidad, suministro y entrega." },
    ],
    leadEyebrow: "SIGUIENTE PASO",
    leadTitle: "¿Qué cal necesita su operación?",
    leadText: "Hable con nuestro equipo por WhatsApp sobre las necesidades de su operación.",
    company: "Empresa", city: "Ciudad / Estado", application: "Aplicación", volume: "Consumo mensual aproximado (opcional)", product: "Producto, si lo conoce (opcional)",
    companyPlaceholder: "Nombre de la empresa", cityPlaceholder: "Ej.: Belo Horizonte / MG", applicationPlaceholder: "Ej.: tratamiento de agua, siderurgia, minería...", volumePlaceholder: "Ej.: 200 t/mes", productPlaceholder: "Ej.: cal calcítica viva", formButton: "Continuar por WhatsApp",
    privacy: "Los campos se usan solo para preparar el mensaje en su dispositivo. El sitio no envía estos datos antes de abrir WhatsApp.",
    messageIntro: "Hola, me gustaría hablar con Quimical sobre suministro de cal industrial.",
  },
} satisfies Record<Language, Record<string, unknown>>;

const clamp = (value: number) => Math.max(0, Math.min(1, value));

const useCalcinationStage = (ref: React.RefObject<HTMLElement>) => {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    let raf = 0;
    let visible = false;
    const element = ref.current;
    if (!element) return;

    const update = () => {
      raf = 0;
      if (!visible || document.hidden) return;
      const rect = element.getBoundingClientRect();
      const total = Math.max(element.offsetHeight - window.innerHeight, 1);
      // Only the four content changes require a React update, not every pixel.
      setStage(Math.min(3, Math.floor(clamp(-rect.top / total) * 4)));
    };

    const onScroll = () => {
      if (visible && !document.hidden && !raf) raf = requestAnimationFrame(update);
    };

    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) onScroll();
      else {
        cancelAnimationFrame(raf);
        raf = 0;
      }
    });
    observer.observe(element);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    document.addEventListener("visibilitychange", onScroll);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      document.removeEventListener("visibilitychange", onScroll);
    };
  }, [ref]);

  return stage;
};

const ConversionStory = () => {
  const { language } = useLanguage();
  const c = content[language] as typeof content.pt;
  const calcRef = useRef<HTMLElement>(null);
  const activeStage = useCalcinationStage(calcRef);

  const whatsappHref = (message?: string) =>
    `https://wa.me/${WHATSAPP_NUMBER}${message ? `?text=${encodeURIComponent(message)}` : ""}`;

  const openWhatsApp = (message?: string) => {
    window.open(whatsappHref(message), "_blank", "noopener,noreferrer");
  };

  const stageIcons = [Factory, Flame, FlaskConical, Droplets];
  const differentialIcons = [Microscope, ShieldCheck, Factory, Route, FlaskConical, Truck];

  return (
    <>
      <section id="home" className="relative min-h-screen overflow-hidden bg-slate-950 text-white pt-20 md:pt-24">
        <div className="absolute inset-0">
          <LiquidHeroImage />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-slate-950/80 via-slate-950/50 to-slate-950/20" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-950/10 via-transparent to-slate-950/90" />
        </div>
        <div className="container relative z-10 mx-auto flex min-h-[calc(100vh-5rem)] max-w-6xl flex-col justify-center px-4 py-16 md:py-24">
          <p className="mb-6 text-xs font-semibold tracking-[0.24em] text-white/65">{c.eyebrow}</p>
          <h1 className="max-w-5xl text-4xl font-semibold leading-[1.04] tracking-[-0.04em] sm:text-5xl md:text-7xl lg:text-8xl">
            {c.heroTitle}
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/72 sm:text-lg md:text-xl">{c.heroText}</p>
          <div className="mt-10">
            <Button size="lg" onClick={() => openWhatsApp()} className="h-12 rounded-full px-7 text-base shadow-xl">
              <MessageCircle className="mr-2 h-5 w-5" />
              {c.whatsapp}
            </Button>
          </div>
          <div className="mt-auto flex items-center gap-2 pt-16 text-xs uppercase tracking-[0.16em] text-white/45">
            <ArrowDown className="h-4 w-4" />
            {c.scroll}
          </div>
        </div>
      </section>

      <section id="decisao" className="bg-white py-24 md:py-36">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:gap-24">
            <div className="lg:sticky lg:top-32 lg:self-start">
              <div>
                <p className="text-xs font-semibold tracking-[0.22em] text-primary">{c.painsEyebrow}</p>
                <h2 className="mt-5 text-3xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-4xl md:text-5xl">{c.painsTitle}</h2>
                <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600 md:text-lg">{c.painsIntro}</p>
              </div>
            </div>
            <div className="space-y-5">
              {c.pains.map((pain, index) => (
                <div key={pain.title}>
                  <article className="group min-h-[240px] rounded-3xl border border-slate-200 bg-slate-50 p-7 transition-colors duration-300 hover:bg-white md:p-9">
                    <div className="flex items-start gap-5">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-950 text-sm font-semibold text-white">0{index + 1}</span>
                      <div>
                        <div className="mb-4 flex items-center gap-2 text-amber-600">
                          <AlertTriangle className="h-4 w-4" />
                          <span className="text-xs font-semibold uppercase tracking-[0.16em]">{c.painsEyebrow}</span>
                        </div>
                        <h3 className="text-2xl font-semibold tracking-[-0.02em] text-slate-950 md:text-3xl">{pain.title}</h3>
                        <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600">{pain.text}</p>
                      </div>
                    </div>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="quimical" className="bg-slate-950 py-24 text-white md:py-36">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold tracking-[0.22em] text-white/50">{c.solutionEyebrow}</p>
            <h2 className="mt-5 text-4xl font-semibold tracking-[-0.04em] md:text-6xl">{c.solutionTitle}</h2>
            <p className="mt-6 text-lg leading-relaxed text-white/65">{c.solutionText}</p>
          </div>
          <div className="mt-16 grid gap-3 md:grid-cols-6">
            {c.flow.map((item, index) => (
              <div key={item}>
                <div className="relative flex min-h-32 flex-col justify-between rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                  <span className="text-xs font-medium text-white/35">0{index + 1}</span>
                  <span className="text-base font-medium text-white">{item}</span>
                  {index < c.flow.length - 1 && <div className="absolute -right-2 top-1/2 hidden h-px w-4 bg-white/25 md:block" />}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Button variant="secondary" onClick={() => openWhatsApp()} className="rounded-full">
              <MessageCircle className="mr-2 h-4 w-4" />
              {c.whatsapp}
            </Button>
          </div>
        </div>
      </section>

      <section id="calcinacao" ref={calcRef} className="relative h-[300vh] bg-[#f3f0e8]">
        <div className="sticky top-0 flex min-h-screen items-center overflow-hidden py-24">
          <div className="container mx-auto grid max-w-6xl gap-12 px-4 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <div>
              <p className="text-xs font-semibold tracking-[0.22em] text-primary">{c.calcEyebrow}</p>
              <h2 className="mt-5 text-3xl font-semibold tracking-[-0.04em] text-slate-950 sm:text-4xl md:text-6xl">{c.calcTitle}</h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">{c.calcIntro}</p>
              <div className="mt-10 h-1 overflow-hidden rounded-full bg-slate-950/10">
                <div className="h-full bg-primary" style={{ width: `${(activeStage + 1) * 25}%` }} />
              </div>
            </div>

            <div className="relative min-h-[420px] rounded-[2rem] bg-slate-950 p-7 text-white shadow-2xl md:min-h-[500px] md:p-10">
              <div className="absolute inset-x-8 top-8 flex justify-between">
                {c.calcStages.map((stage, index) => {
                  const Icon = stageIcons[index];
                  const active = index === activeStage;
                  return (
                    <div key={stage.label} className={`flex h-10 w-10 items-center justify-center rounded-full border ${active ? "border-white bg-white text-slate-950" : "border-white/15 bg-white/5 text-white/35"}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                  );
                })}
              </div>
              <div className="flex min-h-[350px] items-end pt-20 md:min-h-[420px]">
                <div>
                  <p className="text-xs font-semibold tracking-[0.18em] text-white/45">{c.calcStages[activeStage].label}</p>
                  <h3 className="mt-4 text-3xl font-semibold tracking-[-0.03em] md:text-5xl">{c.calcStages[activeStage].title}</h3>
                  <p className="mt-5 max-w-lg text-base leading-relaxed text-white/62 md:text-lg">{c.calcStages[activeStage].text}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="produtos" className="bg-white py-24 md:py-36">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold tracking-[0.22em] text-primary">{c.limeEyebrow}</p>
            <h2 className="mt-5 text-4xl font-semibold tracking-[-0.04em] text-slate-950 md:text-6xl">{c.limeTitle}</h2>
            <p className="mt-6 text-lg leading-relaxed text-slate-600">{c.limeText}</p>
          </div>
          <div className="mt-14 grid gap-4 md:grid-cols-2">
            {c.limeTypes.map((lime) => (
              <div key={lime.name}>
                <article className="min-h-[260px] rounded-3xl border border-slate-200 p-7 md:p-9">
                  <FlaskConical className="h-6 w-6 text-primary" />
                  <p className="mt-9 font-mono text-sm text-primary">{lime.formula}</p>
                  <h3 className="mt-2 text-2xl font-semibold tracking-[-0.02em] text-slate-950 md:text-3xl">{lime.name}</h3>
                  <p className="mt-5 text-sm leading-relaxed text-slate-500">{lime.applications}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="atendimento" className="bg-slate-100 py-24 md:py-36">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
            <div>
              <p className="text-xs font-semibold tracking-[0.22em] text-primary">{c.coverageEyebrow}</p>
              <h2 className="mt-5 text-4xl font-semibold tracking-[-0.04em] text-slate-950 md:text-6xl">{c.coverageTitle}</h2>
              <p className="mt-6 text-base leading-relaxed text-slate-600 md:text-lg">{c.coverageText}</p>
            </div>
            <div>
              <div className="rounded-[2rem] bg-white p-7 shadow-sm md:p-9">
                <div className="flex items-center gap-3 text-slate-950">
                  <MapPin className="h-5 w-5 text-primary" />
                  <span className="text-sm font-semibold">Brasil</span>
                </div>
                <div className="mt-7 space-y-5">
                  {coverageGroups.map((group) => (
                    <div key={group.region} className="grid grid-cols-[120px_1fr] gap-4 border-b border-slate-100 pb-4 last:border-0 last:pb-0">
                      <span className="text-sm font-medium text-slate-500">{group.region}</span>
                      <span className="text-sm font-semibold tracking-wide text-slate-950">{group.states}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-7 text-xs leading-relaxed text-slate-400">{c.coverageNote}</p>
              </div>
            </div>
          </div>
          <div className="mt-10">
            <Button onClick={() => openWhatsApp()} className="rounded-full">
              <MessageCircle className="mr-2 h-4 w-4" />
              {c.checkLocation}
            </Button>
          </div>
        </div>
      </section>

      <section id="diferenciais" className="bg-white py-24 md:py-36">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="max-w-4xl">
            <p className="text-xs font-semibold tracking-[0.22em] text-primary">{c.diffEyebrow}</p>
            <h2 className="mt-5 text-4xl font-semibold tracking-[-0.04em] text-slate-950 md:text-6xl">{c.diffTitle}</h2>
          </div>
          <div className="mt-14 grid gap-x-8 gap-y-10 md:grid-cols-2 lg:grid-cols-3">
            {c.diffs.map((diff, index) => {
              const Icon = differentialIcons[index];
              return (
                <div key={diff.title}>
                  <article className="border-t border-slate-200 pt-6">
                    <Icon className="h-5 w-5 text-primary" />
                    <h3 className="mt-5 text-xl font-semibold text-slate-950">{diff.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{diff.text}</p>
                  </article>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="contato" className="bg-slate-950 py-24 text-white md:py-36">
        <div className="container mx-auto grid max-w-6xl gap-14 px-4 lg:grid-cols-[0.85fr_1.15fr] lg:gap-24">
          <div>
            <p className="text-xs font-semibold tracking-[0.22em] text-white/45">{c.leadEyebrow}</p>
            <h2 className="mt-5 text-4xl font-semibold tracking-[-0.04em] md:text-6xl">{c.leadTitle}</h2>
            <p className="mt-6 text-base leading-relaxed text-white/62 md:text-lg">{c.leadText}</p>
            <div className="mt-8 flex items-center gap-3 text-sm text-white/55">
              <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              WhatsApp
            </div>
          </div>

          <div>
            <HomepageWhatsAppContact />
          </div>
        </div>
      </section>

      <button
        type="button"
        onClick={() => openWhatsApp()}
        className="fixed bottom-5 right-5 z-40 flex h-13 min-h-[52px] items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-white shadow-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary"
        aria-label={c.whatsapp}
      >
        <MessageCircle className="h-5 w-5" />
        <span className="hidden sm:inline">{c.whatsapp}</span>
      </button>
    </>
  );
};

export default ConversionStory;

